/* iter-110 — Reusable money input.
   - Renders a `$` prefix inside the field
   - Auto-formats on blur to a thousands-separated number (e.g. 2400000 → 2,400,000)
   - Keeps the underlying value as a plain numeric string so the consumer
     can still POST it to APIs unchanged
   - Accepts the same className/style/testid props as a bare <input>
*/
import { forwardRef, useCallback, useState, useEffect } from 'react';

function formatWithCommas(raw) {
  if (raw === '' || raw == null) return '';
  // Strip everything except digits, decimal point, leading minus.
  const cleaned = String(raw).replace(/[^0-9.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return cleaned;
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return cleaned;
  // Preserve trailing decimals the user typed.
  const [intPart, decPart] = cleaned.split('.');
  const formatted = Number(intPart).toLocaleString('en-US');
  return decPart != null ? `${formatted}.${decPart}` : formatted;
}

// iter-120 — strict blur formatter: always 2 decimals, thousands-grouped
// so amounts come to rest as $1,000,000.00 per spec.
function formatStrict(raw) {
  if (raw === '' || raw == null) return '';
  const cleaned = String(raw).replace(/[^0-9.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return '';
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return cleaned;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function unformat(formatted) {
  return String(formatted ?? '').replace(/[^0-9.\-]/g, '');
}

const MoneyInput = forwardRef(function MoneyInput(
  { value, onChange, className = '', style = {}, placeholder = '', disabled, ...rest },
  ref,
) {
  // Local display string with commas. Stays in sync with parent value.
  const [display, setDisplay] = useState(() => formatStrict(value));

  useEffect(() => {
    setDisplay(formatStrict(value));
  }, [value]);

  const handleChange = useCallback((e) => {
    const raw = unformat(e.target.value);
    setDisplay(e.target.value); // keep typed string while focused
    // Propagate raw numeric value upstream so APIs stay clean.
    onChange?.({ ...e, target: { ...e.target, value: raw } });
  }, [onChange]);

  const handleBlur = useCallback((e) => {
    // iter-120 — Lock to 2-decimal grouping so e.g. 1000000 displays
    // as $1,000,000.00 the moment the field loses focus.
    const formatted = formatStrict(e.target.value);
    setDisplay(formatted);
    rest.onBlur?.(e);
  }, [rest]);

  const handleFocus = useCallback((e) => {
    // Strip commas while editing so cursor math doesn't fight the user.
    setDisplay(unformat(e.target.value));
    rest.onFocus?.(e);
  }, [rest]);

  return (
    <div className={`relative ${disabled ? 'opacity-60' : ''}`} style={{ display: 'block' }}>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#7A8C8C',
          fontFamily: 'Manrope, sans-serif',
          pointerEvents: 'none',
          lineHeight: 1,
        }}
      >
        $
      </span>
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={{ ...style, paddingLeft: '22px' }}
        {...(() => { const { onBlur, onFocus, ...other } = rest; return other; })()}
      />
    </div>
  );
});

export default MoneyInput;
